import React, { useState, useContext, useEffect } from "react";
import {
    Grid,
    Typography,
    Box,
    List,
    ListItem,
    ListItemIcon,
    Button,
    IconButton,
    TextField,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import { styled } from "@mui/material/styles";
import { DataContext } from "../../context/data-provider";
import {
    ALL_SKILLS,
    DELETE_CURRENT_USER_SKILLS,
    EDIT_USER_URL,
    SKILLS_PER_USER_URL,
} from "../../services/endpoints";
import axiosInstance from "../../services/axios-instance";
import { SkillsInterface } from "../../interface/skillsInterface";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import LanguageIcon from "@mui/icons-material/Language";
import DeleteIcon from "@mui/icons-material/Delete";
import { multipartFormDataConfig } from "../../assets/data/multipartFormDataConfig";
import { getUrlWithProfilePicture } from "../utils/getUrlWithProfilePicture";

const BorderLine = styled("div")({
    marginTop: "12px",
    border: "1px solid #f9fafc",
});

const SkillName = styled("span")({
    fontWeight: "bold",
    marginRight: "8px",
});

const SearchContainer = styled("div")`
    margin-bottom: 16px;
`;

const SearchInput = styled("input")`
    padding: 8px;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: 16px;
    margin-top: 8px;
    margin-left: 12px;
`;

const SkillList = styled("ul")`
    list-style-type: none;
    padding-right: 8px;
    padding: 0;
    margin: 0;
`;

const SkillItem = styled("li")`
    margin-bottom: 8px;
    display: flex; /* Add display flex */
    align-items: center; /* Align items vertically */
`;

const SmallButton = styled(Button)({
    "&.MuiButtonBase-root": {
        padding: "1px 1px",
        fontSize: "14px",
    },
});
const Skills = () => {
    const { currentUser, setCurrentUser } = useContext(DataContext);
    const [currentUserSkills, setCurrentUserSkills] = useState<
        SkillsInterface[]
    >([]);

    const [, setSelectedSkills] = useState<number[]>(
        currentUserSkills.map((skill) => skill.id)
    );

    const [allSkills, setallSkills] = useState<SkillsInterface[]>([]);

    useEffect(() => {
        async function fetchData() {
            try {
                if (currentUser.id) {
                    const response = await axiosInstance.get(ALL_SKILLS);
                    const responseUserSkills = await axiosInstance.get(
                        SKILLS_PER_USER_URL(currentUser.id)
                    );
                    setCurrentUserSkills(responseUserSkills.data.skills);
                    setallSkills(response.data.skills);
                }
            } catch (error) {
                console.error(error);
            }
        }
        fetchData();
    }, [currentUser.id]);

    const [searchQuery, setSearchQuery] = useState("");
    const [filteredSkills, setFilteredSkills] = useState(allSkills);

    const handleSearch = (event: { target: { value: any } }) => {
        const { value } = event.target;
        setSearchQuery(value);

        // Perform the search/filtering based on the search query
        const filtered = allSkills.filter((skill) =>
            skill.name.toLowerCase().includes(value.toLowerCase())
        );
        setFilteredSkills(filtered);
    };

    const handleAddSkill = async (skillId: number) => {
        if (currentUser.id)
            try {
                const response = await axiosInstance.put(
                    SKILLS_PER_USER_URL(currentUser.id),
                    {
                        skills: [skillId],
                    }
                );
                setCurrentUserSkills(response.data.user.skills);
                setSelectedSkills(
                    response.data.user.skills.map(
                        (skill: { id: number }) => skill.id
                    )
                );
            } catch (error) {
                // Handle error if the POST request fails
            }
    };

    const handleDelete = async (skillId: number) => {
        if (currentUser.id)
            try {
                const response = await axiosInstance.delete(
                    DELETE_CURRENT_USER_SKILLS(currentUser.id, skillId)
                );
                setCurrentUserSkills(response.data.user.skills);
                setSelectedSkills(
                    response.data.user.skills.map(
                        (skill: { id: number }) => skill.id
                    )
                );
            } catch (error) {
                // Handle error if the DELETE request fails
                console.error(error);
            }
    };

    // update user name
    const [updatedText, setUpdatedText] = useState(currentUser.name);
    const handleInputChange = (event: {
        target: { value: React.SetStateAction<string> };
    }) => {
        setUpdatedText(event.target.value);
    };

    // upload profile picture
    const [uploadedPicture, setUploadedPicture] = useState<File | null>(null);
    const handlePictureUpload = (
        event: React.ChangeEvent<HTMLInputElement>
    ): void => {
        const file = event.target.files?.[0];
        if (file) {
            setUploadedPicture(file);
        }
    };

    const handleEditUserInfo = async () => {
        try {
            const formData = new FormData();

            formData.append("name", updatedText);
            uploadedPicture &&
                formData.append("profile_picture", uploadedPicture);

            const responseEditUser = await axiosInstance.post(
                EDIT_USER_URL,
                formData,
                multipartFormDataConfig
            );

            setCurrentUser(responseEditUser?.data?.data);
        } catch (error) {
            console.error(
                "An error occurred during the user info update:",
                error
            );
        }
    };

    // edit UI
    const [editMode, setEditMode] = useState(false);
    const handleEditModeToggle = () => {
        setEditMode(!editMode);

        if (editMode) {
            handleEditUserInfo();
        }
    };

    useEffect(() => {
        if (editMode) {
            setUpdatedText(currentUser.name);
        }
    }, [editMode, currentUser.name]);

    return (
        <Grid container spacing={2} style={{ display: "flex" }}>
            <Grid item xs={12} md={12}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        backgroundColor: "#f9fafc",
                        borderRadius: "4px",
                        padding: 4,
                    }}
                >
                    {editMode ? (
                        <input
                            type="file"
                            accept=".jpg, .jpeg, .png"
                            onChange={handlePictureUpload}
                        />
                    ) : (
                        <img
                        style={{
                            height: 120,
                            width: 120,
                            objectFit: "cover",
                            objectPosition: currentUser?.profile_picture_focal_point ? currentUser?.profile_picture_focal_point : "center",
                            borderRadius: "50%"
                        }}
                        src={getUrlWithProfilePicture(currentUser?.profile_picture)}
                    />
                    )}
                    <Box>
                        {editMode ? (
                            <TextField
                                value={updatedText}
                                onChange={handleInputChange}
                                autoFocus
                            />
                        ) : (
                            <Typography variant="h6" fontWeight="bold">
                                {currentUser.name}
                            </Typography>
                        )}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            <EmailIcon />
                            <Typography variant="subtitle1">
                                {currentUser.email}
                            </Typography>
                        </Box>
                    </Box>
                    <Button
                        color={editMode ? "success" : "warning"}
                        onClick={handleEditModeToggle}
                    >
                        {editMode ? "Save" : "Edit"}
                    </Button>
                </Box>
            </Grid>

            <Grid item xs={12} md={12}>
                <Box
                    sx={{
                        alignItems: "center",
                        gap: 2,
                        backgroundColor: "#f9fafc",
                        borderRadius: "4px",
                        padding: 4,
                    }}
                >
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                        What to Know Before You Start
                    </Typography>
                    <Typography variant="body1" color="grey" gutterBottom>
                        Select your level of knowledge for each technology:
                    </Typography>
                    <BorderLine />
                    <Grid container spacing={2}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={8}>
                                <SearchContainer>
                                    <SearchInput
                                        type="text"
                                        value={searchQuery}
                                        onChange={handleSearch}
                                        placeholder="Search by skill name"
                                    />
                                </SearchContainer>
                                {searchQuery !== "" && (
                                    <SkillList>
                                        {filteredSkills.map((skill) => (
                                            <SkillItem key={skill.id}>
                                                <SkillName>
                                                    {skill.name}
                                                </SkillName>
                                                <SmallButton
                                                    variant="contained"
                                                    color="info"
                                                    onClick={() =>
                                                        handleAddSkill(skill.id)
                                                    }
                                                >
                                                    Add
                                                </SmallButton>
                                            </SkillItem>
                                        ))}
                                    </SkillList>
                                )}
                            </Grid>
                        </Grid>
                    </Grid>
                </Box>
            </Grid>
            <Grid item xs={12} md={12}>
                <Box sx={{ p: 2, borderRadius: 4, backgroundColor: "#f9fafc" }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                        List of your skills
                    </Typography>
                    <List>
                        {currentUserSkills.map((skill, index) => (
                            <React.Fragment key={skill.id}>
                                <ListItem>
                                    <ListItemIcon>
                                        <LanguageIcon />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <SkillName>{skill.name}</SkillName>
                                        }
                                    />
                                    <IconButton
                                        onClick={() => handleDelete(skill.id)}
                                        edge="start"
                                        aria-label="delete"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </ListItem>
                                {index < currentUserSkills.length - 1 && (
                                    <Divider />
                                )}
                            </React.Fragment>
                        ))}
                    </List>
                </Box>
            </Grid>
        </Grid>
    );
};

export default Skills;
